import { useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { usePageAccessGuard } from "@/hooks/usePageAccessGuard";
import { useSimplifiedUserData } from "@/providers/SimplifiedUserDataProvider";
import { UnifiedItemsContainer } from "@/components/layout/page-containers";
import { PageHandlers, TransformContext } from "@/components/layout/page-containers/UnifiedItemsContainer/types";
import { flowsConfig } from "./unified-items/configs/flowsConfig";
import { CardData } from "@/components/layout/element-layout/grid-layout/GridLayout";

// Import stores and types
import {
  LogicFlow,
  logicFlowStore,
  createLogicFlow,
  updateLogicFlow,
  deleteLogicFlow,
  fetchLogicFlows,
} from "@/lib/stores/logicFlowStore";
import { flowsDataSource } from "@/lib/dataSources/sources/flows";
import GlossaryWidget from "@/components/cards/widgets/ObjectsWidget";

/**
 * LogicPage - SOURCE OF TRUTH for logic flow operations
 * 
 * This page owns all logic flow-specific:
 * - Store operations (write/update/delete in logicFlowStore)
 * - CRUD handlers (create, update, delete flows)
 * - Card transformation (raw LogicFlow → CardData)
 * - Wizard callbacks (what happens after create/update)
 * 
 * When the structure of logic flows changes, update THIS file.
 */
export default function LogicPage() {
  usePageAccessGuard('flows');
  
  const { toast } = useToast();
  const navigate = useNavigate();
  const { selectedGame } = useSimplifiedUserData();

  // #region Store Operations
  /**
   * Logic flow-specific store operations
   */
  const storeOperations = useMemo(() => ({
    writeToStore: (items: LogicFlow[]) => {
      logicFlowStore.setData(items);
    },
    updateInStore: (id: string, data: Partial<LogicFlow>) => {
      const current = logicFlowStore.getData();
      const updated = current.map(item => 
        item.id === id ? { ...item, ...data } : item
      );
      logicFlowStore.setData(updated);
    },
    deleteFromStore: (id: string) => {
      const current = logicFlowStore.getData();
      logicFlowStore.setData(current.filter(item => item.id !== id));
    },
  }), []);
  // #endregion

  // #region CRUD Handlers
  /**
   * Logic flow-specific CRUD handlers
   */
  const handlers = useMemo(() => ({
    onCreate: async (data: any, folderId?: string): Promise<LogicFlow> => {
      if (!selectedGame?.id) {
        throw new Error("No game selected");
      }

      const flowData = {
        ...data,
        game_id: data.game_id || selectedGame.id,
        folder: folderId || data.folder || null,
      };

      const createdFlow = await createLogicFlow(flowData, toast);
      
      // Update local store
      const current = logicFlowStore.getData();
      logicFlowStore.setData([...current, createdFlow]);
      
      return createdFlow;
    },

    onUpdate: async (id: string, data: Partial<LogicFlow>): Promise<LogicFlow> => {
      if (!selectedGame?.id) {
        throw new Error("No game selected");
      }

      const existingFlow = logicFlowStore.getData().find(item => item.id === id);
      if (!existingFlow) {
        throw new Error("Logic flow not found");
      }

      const flowData = {
        ...existingFlow,
        ...data,
        id,
        game_id: data.game_id || selectedGame.id,
      };

      const updatedFlow = await updateLogicFlow(flowData, toast);
      storeOperations.updateInStore(id, updatedFlow);
      
      return updatedFlow;
    },

    onDelete: async (id: string): Promise<void> => {
      if (!selectedGame?.id) {
        throw new Error("No game selected");
      }

      await deleteLogicFlow(id, selectedGame.id, toast);
      storeOperations.deleteFromStore(id);
    },

    onBulkDelete: async (ids: string[]): Promise<void> => {
      if (!selectedGame?.id) {
        throw new Error("No game selected");
      }

      for (const id of ids) {
        await deleteLogicFlow(id, selectedGame.id, toast);
        storeOperations.deleteFromStore(id);
      }
    },
  }), [selectedGame, toast, storeOperations]);
  // #endregion

  // #region Card Transformation
  /**
   * Transform logic flow items to CardData for display
   */
  const transformToCard = useCallback((item: LogicFlow, context: TransformContext): CardData => {
    const cards = flowsDataSource.transformers.toCard?.([item], {
      tags: context.tags,
      blueprints: context.blueprints || [],
      getUserNameById: context.getUserNameById,
    }) || [];
    
    return cards[0] || {
      id: item.id,
      title: item.name,
      gameId: item.game_id,
      folder: item.folder,
    };
  }, []);

  /**
   * Render a logic flow card
   */
  const renderCard = useCallback((card: CardData, tags: any[]) => {
    return (
      <GlossaryWidget
        id={card.id}
        gameId={card.gameId}
        title={card.title}
        owner={card.owner}
        tags={card.tags}
        description={card.description}
        lastUpdate={card.lastUpdate}
        version={card.version}
        status={card.status}
        folder={card.folder as string}
        itemType="flows"
        icon={card.icon}
        iconBg={card.iconBg}
        shadowType={card.shadowType}
        shadowColor={card.shadowColor}
        hasVersionMismatch={card.hasVersionMismatch}
        onDelete={card.onDelete}
        onEdit={card.onEdit}
      />
    );
  }, []);
  // #endregion

  // #region Edit Handler
  /**
   * Handle edit action for logic flows
   */
  const onEdit = useCallback((id: string, data: LogicFlow[], navigateFn: (path: string) => void) => {
    const flow = data.find(item => item.id === id);
    navigate(`/logic/${id}`, { state: { itemName: flow?.name } });
  }, [navigate]);
  // #endregion

  // #region Wizard Callbacks
  /**
   * Logic flow-specific wizard callbacks
   */
  const wizardCallbacks = useMemo(() => ({
    onItemCreated: async (item: LogicFlow) => {
      toast({
        title: "Success",
        description: `Logic flow "${item.name}" created successfully`,
      });
      
      if (selectedGame?.id) {
        try {
          await fetchLogicFlows(selectedGame.id);
        } catch (error) {
          console.warn("Failed to refresh logic flows after creation:", error);
        }
      }
    },

    onItemUpdated: async (item: LogicFlow) => {
      toast({
        title: "Success",
        description: `Logic flow "${item.name}" updated successfully`,
      });
      
      if (selectedGame?.id) {
        try {
          await fetchLogicFlows(selectedGame.id);
        } catch (error) {
          console.warn("Failed to refresh logic flows after update:", error);
        }
      }
    },

    onWizardClose: () => {},
  }), [toast, selectedGame]);
  // #endregion

  // #region Combined PageHandlers
  const pageHandlers: PageHandlers<LogicFlow> = useMemo(() => ({
    storeOperations,
    handlers,
    transformToCard,
    renderCard,
    wizardCallbacks,
    onEdit,
    features: {
      supportsPendingChanges: false,
      supportsBulkCreate: false,
      supportsFlowView: false,
    },
  }), [storeOperations, handlers, transformToCard, renderCard, wizardCallbacks, onEdit]);
  // #endregion

  return (
    <UnifiedItemsContainer 
      itemType="flows"
      handlers={pageHandlers}
      config={flowsConfig}
    />
  );
}
